/**
 * Field roster: select one to work on, tick several for a batch run, and say what to do next.
 *
 * It is the LEFT SIDEBAR of the workspace, full height, mirroring the console on the right.
 * It used to be a 13.5 rem floating panel stacked under the drawing tools, and that width
 * was not a taste question: the row could not give the field name any width at all (see
 * `.k-fieldrow` in styles/layout.css), so the one thing you pick a field by was the one
 * thing you could not read. Full height also means the list scrolls instead of being capped
 * at 15 rem with the rest of the fields below the fold.
 *
 * WHY THE ROW NOW CARRIES A PRIORITY, AND WHY THE ROSTER CARRIES A SORT. The account quota
 * is twenty fields. At twenty rows a flat list stops answering the only question anybody
 * brings to it — which of these do I drive to — and the report that arrived ("which plot has
 * priority for searching") is that question, not a request for a checkbox. Three levels
 * (`Высокий / Средний / Без приоритета`), because:
 *
 *   * a single flag is a SET, not an ORDER: it splits twenty rows into eight and twelve, and
 *     the eight are as unordered as the twenty were. It also has nowhere to demote to, so
 *     "I looked and it can wait" and "I never marked this" become the same state;
 *   * a free number is an order nobody keeps. Inserting between 3 and 4 means renumbering,
 *     nobody renumbers, and within a month the ranking says less than the flag would;
 *   * three is where it stops. The bottom level is the ABSENCE of a mark, not a third tier,
 *     so the default costs nothing and never competes with anything else in the row. (It used
 *     to share the screen with «Следить за полем»; that feature was removed on 2026-10-02, and
 *     the mark is now the only thing a digger says about a field without opening it.)
 *
 * The mark is a WORD in the row, not a colour: «Высокий» and «Средний» read the same in either
 * theme and to anyone reading with a screen reader.
 *
 * THE WAY INTO THE EDITOR HAS A RESERVED PLACE (UX_SPEC §13.2), and it took two attempts.
 * It began as a 16 px pencil with `border-color: transparent` at rest, all of whose words
 * lived in `aria-label` and `title`, repeated up to twenty times at the far edge of a 15 rem
 * column — measured affordances leading to edit and visible at rest: ZERO. The answer to that
 * was an action strip carrying the WORD «Изменить», opened under the selected row, and it
 * brought its own defect: selecting row 7 pushed rows 8..20 down by the height of the strip,
 * so the row you were about to click next moved out from under the cursor. The pencil is back,
 * but on a slot every row owns: only its VISIBILITY changes (hover, keyboard focus, and always
 * on the selected row), the row's height is a constant, and the neighbours never move. It also
 * SELECTS the field it opens, so the contour being renamed is the one drawn, highlighted and
 * described by the console. Row click keeps meaning "select and draw on the map" — it costs
 * three GETs and half the screen, so it cannot also mean "edit".
 *
 * AND NO PHASE OF LOADING DELETES THE LIST. The early `return` on `loading` threw a perfectly
 * good list away over a flag: on every refetch the column emptied, the scroll position was
 * lost with the node, focus went to BODY and `aria-pressed` vanished mid-press — which is the
 * "блик" the owner reported. `loading && fields.length === 0` is the whole fix.
 *
 * The roster has no name filter, and that is deliberate: the search box over the map already
 * matches your own fields by substring (CoordSearch). What was missing was order.
 *
 * GROUPS ARE THE OUTER KEY AND THE SORT IS THE INNER ONE. A group is a free name somebody
 * typed on the field, not an entry from a directory: there is no directory of Russian
 * districts here and at a quota of twenty fields there is no need for one. The only thing
 * that has to hold is that the same name collects the same fields, which is why saving folds
 * case and substitutes the spelling that already exists. Headings appear only once at least
 * one field carries a group — a caption reading «Без группы» over a list with no groups in it
 * is a label for a distinction nobody has drawn.
 *
 * DELETING LIVES IN THE CARD, NEXT TO ARCHIVING. They are the two ways a field leaves the
 * list and the choice between them is made once, so they are read together: archiving keeps
 * everything that was computed, deleting takes it. The hint says how many analyses that is,
 * because that is the part the row does not show and the part you cannot get back.
 */

import { useEffect, useId, useMemo, useRef, useState } from "react";

import { T } from "../strings";
import type { Field, FieldPatch, FieldPriority } from "../api/types";
import { formatFull, hectares } from "../lib/format";
import type { QueueEntry } from "../state/useWorkspace";
import { Glyph, glyph } from "./DrawTools";

/**
 * The batch mark, and `null` when the field is not in a batch.
 *
 * It used to return `k-dot--none` for "no entry", so every row at rest carried an empty
 * square standing against the batch CHECKBOX — two empty squares in a row, one of them a
 * control and one of them a reading, and the reading was empty on all twenty (UX_SPEC §7.2).
 */
const queueDot = (status: QueueEntry["status"] | undefined): string | null => {
  switch (status) {
    case "running":
      return "k-dot k-dot--queued";
    case "done":
      return "k-dot k-dot--free";
    case "error":
      return "k-dot k-dot--nodata";
    case "queued":
      return "k-dot k-dot--none";
    default:
      return null;
  }
};

export type SortKey = "priority" | "name" | "area";

const SORT_STORAGE_KEY = "kopateli.fieldSort";
/** Which group headings are folded shut. Same store and same reason as the sort. */
const CLOSED_GROUPS_STORAGE_KEY = "kopateli.fieldGroupsClosed";

/** A sort you have to re-pick on every load is a sort nobody uses. Same store as the dev panel. */
const readSort = (): SortKey => {
  try {
    const stored = window.localStorage.getItem(SORT_STORAGE_KEY);
    return stored === "name" || stored === "area" ? stored : "priority";
  } catch {
    return "priority";
  }
};

/**
 * Sorting is done here and not asked of the server, because the server already answers in
 * the one order that has to be right without a click — priority first, newest next.
 *
 * `Array.sort` is stable, and that is what the two other keys rely on: sorting by name or by
 * area DOES replace the priority order (that is what picking another key means), but rows
 * that tie on the chosen key keep the server's sequence instead of shuffling between reads.
 * "priority" is therefore a no-op on a list the server just sorted, and kept explicit so the
 * three options read the same way.
 */
export const sortFields = (fields: Field[], key: SortKey): Field[] => {
  const sorted = [...fields];
  if (key === "priority") sorted.sort((a, b) => b.priority - a.priority);
  if (key === "name") sorted.sort((a, b) => a.name.localeCompare(b.name, "ru"));
  if (key === "area") sorted.sort((a, b) => b.area_ha - a.area_ha);
  return sorted;
};

const readClosedGroups = (): string[] => {
  try {
    const parsed: unknown = JSON.parse(
      window.localStorage.getItem(CLOSED_GROUPS_STORAGE_KEY) ?? "[]",
    );
    return Array.isArray(parsed)
      ? parsed.filter((item): item is string => typeof item === "string")
      : [];
  } catch {
    return [];
  }
};

// The same ceilings the server and the database enforce (`FIELD_NOTE_MAX_LENGTH` in
// api/schemas.py, `fields_note_check` in migration 009). Enforced in the box as well, because
// the alternative is learning about the cap from a 422 after typing.
const NAME_MAX_LENGTH = 200;
const NOTE_MAX_LENGTH = 2000;
const GROUP_MAX_LENGTH = 120;

/** A group with no name is not a group: the box is a free field and blank clears it. */
export const normaliseGroup = (value: string): string | null => value.trim() || null;

/**
 * The spelling that WINS is the one already in the list.
 *
 * Without this, «Тверская» and «тверская» are two headings over one intent, and the roster
 * splits fields the owner meant to keep together. Case-insensitive because that is the whole
 * failure people actually make; nothing else about the string is touched, so «Тверская обл.»
 * and «Тверская область» remain two groups — they are two different names.
 */
export const matchExistingGroup = (value: string | null, existing: string[]): string | null => {
  if (value === null) return null;
  const folded = value.toLocaleLowerCase("ru");
  return existing.find((name) => name.toLocaleLowerCase("ru") === folded) ?? value;
};

export interface FieldGroup {
  /** `null` is the ungrouped remainder, and it always sorts last. */
  name: string | null;
  fields: Field[];
}

/**
 * Split the roster by group, then sort inside each one.
 *
 * The order of the groups is the alphabet and nothing else — a group has no priority of its
 * own and inventing one (by size, by the best field in it) would put the chosen sort in
 * second place behind a rule nobody asked for. Inside a group the sort is the one the user
 * picked, which is the whole point of it being the inner key.
 */
export const groupFields = (fields: Field[], sort: SortKey): FieldGroup[] => {
  const byName = new Map<string, Field[]>();
  const ungrouped: Field[] = [];
  for (const field of fields) {
    const group = normaliseGroup(field.group_name ?? "");
    if (group === null) ungrouped.push(field);
    else byName.set(group, [...(byName.get(group) ?? []), field]);
  }
  const groups: FieldGroup[] = [...byName.keys()]
    .sort((a, b) => a.localeCompare(b, "ru"))
    .map((name) => ({ name, fields: sortFields(byName.get(name) ?? [], sort) }));
  if (ungrouped.length > 0 || groups.length === 0) {
    groups.push({ name: null, fields: sortFields(ungrouped, sort) });
  }
  return groups;
};

const PRIORITY_LABEL: Record<FieldPriority, string> = {
  0: T.fieldCard.priority.none,
  1: T.fieldCard.priority.later,
  2: T.fieldCard.priority.go,
};

/**
 * One press, and the FIRST press of an unmarked field gives it the top mark.
 *
 * The commonest act is "these three are Saturday", so 0 → 2 has to be one press, not two. From
 * there it steps down and round: 2 → 1 → 0. The cost is named: the cycle offers no direct pick,
 * so clearing «Высокий» takes two presses — cheaper than three chips that do not fit the column.
 * The control lives in the editor now (the strip that used to hold it is gone with the jump it
 * caused), but it still sends its own PATCH on its own click rather than waiting for «Сохранить»:
 * the row's edge repaints as you press, which is the whole point of a one-press mark.
 */
const nextPriority = (priority: FieldPriority): FieldPriority =>
  priority === 0 ? 2 : priority === 2 ? 1 : 0;

/** Nothing is printed for the neutral level: an "unmarked" tag on most rows is noise. */
const priorityTag = (priority: FieldPriority): string | null =>
  priority === 2 ? T.fieldCard.priority.go : priority === 1 ? T.fieldCard.priority.later : null;

/**
 * The mark is ALSO an edge, 3 px, on the left of the row (UX_SPEC §7.7, §13.2).
 *
 * The word stays: colour alone would leave the mark unreadable to a screen reader and in the
 * one theme neither of us picked. What the edge buys is the ORDER — sorted by priority, the
 * list reads as bands down the left margin instead of as twenty rows you have to read.
 * The edge is 3 px in every state including "no mark", where it is merely transparent, so the
 * row cannot change width by being marked.
 */
const priorityEdge = (priority: FieldPriority): string =>
  priority === 2 ? " k-fieldrow--go" : priority === 1 ? " k-fieldrow--later" : "";

/** How long «Сохранено» stands in the foot. The row itself keeps the change; this announces it. */
const SAVED_NOTICE_MS = 4000;

interface Props {
  fields: Field[];
  loading: boolean;
  error: string | null;
  selectedFieldId: number | null;
  checkedFieldIds: number[];
  queue: QueueEntry[];
  onSelect: (fieldId: number) => void;
  onToggleCheck: (fieldId: number) => void;
  onCheckAll: () => void;
  onClearChecked: () => void;
  /**
   * WHAT THE CHECKBOXES ARE FOR, AND IT HAD NOWHERE TO BE PRESSED.
   *
   * The batch run lived in the developer panel alone, and once that panel went behind
   * Ctrl+Alt+D the twenty checkboxes in this column became a control with no verb: you could
   * tick fields and then find nothing on the screen that did anything with them. The panel
   * keeps its copy — it is where a batch is DEBUGGED, with the date and the flags beside it —
   * and this one is where it is ORDERED, next to the ticks that fill it.
   */
  onRunBatch: () => void;
  /** A run is already in flight. Same latch the console's own button uses. */
  runBusy: boolean;
  onRetry: () => void;
  onUpdateField: (fieldId: number, patch: FieldPatch) => Promise<Field | null>;
  /**
   * The second way out of the list, and the irreversible one. The map toolbar keeps its own
   * delete button: the field you are looking at on the map is not always the one whose card
   * is open, and the two surfaces answer different moments.
   */
  onDeleteField: (fieldId: number) => Promise<boolean>;
  /**
   * Why the roster needs it: a refusal has to be readable where the button was pressed. The
   * quota refusal on the way back out of the archive used to travel to the console, ~1000 px
   * away across the map, and the sidebar's own text was character-for-character unchanged.
   */
  actionError: string | null;
  /** Whether the request behind `fields` asked for archived rows too. */
  showArchived: boolean;
  onShowArchived: (show: boolean) => void;
  /**
   * Сколько ЗАМЕТОК у выбранного поля и как к ним попасть.
   *
   * ВТОРОЙ ВХОД, и вот зачем он: заметки живут в консоли, по другую сторону карты, а карточка
   * в этой колонке — место, где человек уже думает про это самое поле: читает его описание,
   * меняет очередь поездки. «По нему есть четыре заметки» — часть той же мысли, и не знать
   * этого стоит ходки через весь экран. При нуле строки НЕТ вовсе: пустой счёт никуда не зовёт
   * и ничего не говорит.
   *
   * Считаются ЗАМЕТКИ, а не фотографии (notes-v2): фотография перестала быть самостоятельной
   * вещью, и счёт кадров отвечал бы не на тот вопрос — «сколько я там снял» вместо «сколько
   * раз я там был».
   *
   * Карточка раскрывается только на выбранном поле — том, которое показывает консоль, — так
   * что одного числа достаточно: это не построчная величина.
   */
  noteCount: number;
  onShowNotes: () => void;
}

/** The editor holds the two typed properties. Priority is a row control and archiving is a
 *  lifecycle act, so neither rides along with a rename any more. */
interface FormState {
  name: string;
  group: string;
  note: string;
}

const formOf = (field: Field): FormState => ({
  name: field.name,
  group: field.group_name ?? "",
  note: field.note ?? "",
});

const scrollIntoView = (node: HTMLElement | null): void => {
  // Guarded because jsdom has no `scrollIntoView`, the same guard and the same reason as
  // CoordSearch: pulling a row into view must not depend on the environment providing it.
  if (node !== null && typeof node.scrollIntoView === "function") {
    node.scrollIntoView({ block: "nearest" });
  }
};

export function FieldList({
  fields,
  loading,
  error,
  selectedFieldId,
  checkedFieldIds,
  queue,
  onSelect,
  onToggleCheck,
  onCheckAll,
  onClearChecked,
  onRunBatch,
  runBusy,
  onRetry,
  onUpdateField,
  onDeleteField,
  actionError,
  showArchived,
  onShowArchived,
  noteCount,
  onShowNotes,
}: Props) {
  const [sort, setSort] = useState<SortKey>(readSort);
  const [closedGroups, setClosedGroups] = useState<string[]>(readClosedGroups);
  const groupListId = useId();
  const [editingId, setEditingId] = useState<number | null>(null);
  /**
   * Drafts by field id, not one form for the whole roster. With a single form, opening another
   * row wiped what had been typed with no warning — measured. Keyed, there is nothing to lose:
   * the editor reopened shows the text back.
   */
  const [drafts, setDrafts] = useState<Record<number, FormState>>({});
  const [saving, setSaving] = useState(false);
  /** Escape with unsaved edits raises a question in place instead of closing. */
  const [askDiscard, setAskDiscard] = useState(false);
  const [failedId, setFailedId] = useState<number | null>(null);
  const [savedNotice, setSavedNotice] = useState(false);
  const [justArchivedId, setJustArchivedId] = useState<number | null>(null);
  /** Which card's delete question is armed. Disarms on its own after five seconds. */
  const [deleteArmedId, setDeleteArmedId] = useState<number | null>(null);
  /** A row that vanishes without a word reads as a failure, not as a deletion. */
  const [deletedNotice, setDeletedNotice] = useState(false);

  const editButton = useRef<HTMLButtonElement | null>(null);
  /**
   * The editor REPLACES the list, so the pencil that opened it is not in the DOM while it is
   * open: focusing it on close has to wait for the list to come back. The flag is read by the
   * effect below, which runs after the commit that re-renders the roster.
   */
  const returnFocus = useRef(false);
  const nameInput = useRef<HTMLInputElement | null>(null);
  const selectedRow = useRef<HTMLButtonElement | null>(null);
  const archivedGroup = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    try {
      window.localStorage.setItem(SORT_STORAGE_KEY, sort);
    } catch {
      // Private mode, quota, a browser with storage off: the sort still works this session.
    }
  }, [sort]);

  // A card open over a field that has gone (deleted, archived out of view, another account)
  // would edit an id nobody can see.
  useEffect(() => {
    if (editingId !== null && !fields.some((field) => field.id === editingId)) {
      setEditingId(null);
      setAskDiscard(false);
    }
  }, [editingId, fields]);

  /**
   * The selection moved elsewhere — the map, the search box, a batch step. The editor closes
   * and the DRAFT STAYS: the map is the product and the editor is not, so an open form must
   * never block a click on a contour, and nothing typed may be thrown away for it either.
   */
  useEffect(() => {
    if (editingId === null || editingId === selectedFieldId) return;
    setEditingId(null);
    setAskDiscard(false);
  }, [selectedFieldId, editingId]);

  // Caret at the END and nothing selected: select-all is right for a draft prefilled with
  // coordinates, where typing over it is the expected act; on a name somebody typed by hand
  // the first keystroke would destroy it.
  useEffect(() => {
    if (editingId === null) return;
    const input = nameInput.current;
    if (input === null) return;
    input.focus();
    input.setSelectionRange(input.value.length, input.value.length);
  }, [editingId]);

  // Focus used to land in BODY on every close, so the keyboard lost its place.
  useEffect(() => {
    if (editingId !== null || !returnFocus.current) return;
    returnFocus.current = false;
    editButton.current?.focus();
  }, [editingId]);

  useEffect(() => {
    if (!savedNotice) return;
    const timer = window.setTimeout(() => setSavedNotice(false), SAVED_NOTICE_MS);
    return () => window.clearTimeout(timer);
  }, [savedNotice]);

  useEffect(() => {
    if (!deletedNotice) return;
    const timer = window.setTimeout(() => setDeletedNotice(false), SAVED_NOTICE_MS);
    return () => window.clearTimeout(timer);
  }, [deletedNotice]);

  // Five seconds, the same as the map toolbar's. An armed irreversible question left standing
  // is one stray click from being answered by accident.
  useEffect(() => {
    if (deleteArmedId === null) return;
    const timer = window.setTimeout(() => setDeleteArmedId(null), 5000);
    return () => window.clearTimeout(timer);
  }, [deleteArmedId]);

  /** Closing the card, or opening another one, takes the question with it. */
  useEffect(() => setDeleteArmedId(null), [editingId]);

  const live = useMemo(
    () =>
      sortFields(
        fields.filter((field) => !field.archived_at),
        sort,
      ),
    [fields, sort],
  );
  const archived = useMemo(
    () =>
      sortFields(
        fields.filter((field) => field.archived_at),
        sort,
      ),
    [fields, sort],
  );
  const groups = useMemo(() => groupFields(live, sort), [live, sort]);
  /** No group anywhere means no headings at all — not one heading saying «Без группы». */
  const hasGroups = groups.some((group) => group.name !== null);
  /**
   * Every name in use, for the datalist and for folding case on save. Taken from `fields`
   * and not from `live`: a name only an archived field carries is still a name in use, and
   * offering it back is what keeps that field in its old group when it returns.
   */
  const existingGroups = useMemo(() => {
    const names = new Set<string>();
    for (const field of fields) {
      const group = normaliseGroup(field.group_name ?? "");
      if (group !== null) names.add(group);
    }
    return [...names].sort((a, b) => a.localeCompare(b, "ru"));
  }, [fields]);

  /**
   * Folded headings are remembered, but only the ones that still exist.
   *
   * The unfiltered write grew the key for ever: a group renamed or emptied left its name in
   * store, and «Тверская» folded once in July was still being carried around in September
   * with nothing to fold. Nothing is written while the roster is empty — a load that has not
   * arrived yet is not evidence that a group is gone.
   */
  useEffect(() => {
    if (fields.length === 0) return;
    const alive = closedGroups.filter((name) => existingGroups.includes(name));
    try {
      window.localStorage.setItem(CLOSED_GROUPS_STORAGE_KEY, JSON.stringify(alive));
    } catch {
      // Same as the sort: folding still works this session, it just does not survive a reload.
    }
  }, [closedGroups, existingGroups, fields.length]);

  // The selected row is pulled into view wherever the selection came from — the map, the
  // search box, the console. At twenty fields only ~15 rows fit, so a field picked by clicking
  // its contour was highlighted in a row nobody could see.
  useEffect(() => {
    scrollIntoView(selectedRow.current);
  }, [selectedFieldId]);

  // An announcement below the fold is the same "nothing happened" it is meant to cure: with a
  // full account the archived group stands past every live row.
  useEffect(() => {
    if (!showArchived) {
      setJustArchivedId(null);
      return;
    }
    scrollIntoView(archivedGroup.current);
  }, [showArchived, archived.length]);

  const draftOf = (field: Field): FormState => drafts[field.id] ?? formOf(field);

  const setDraft = (fieldId: number, next: Partial<FormState>, current: FormState): void => {
    setDrafts((all) => ({ ...all, [fieldId]: { ...current, ...next } }));
  };

  const dropDraft = (fieldId: number): void => {
    setDrafts((all) => {
      const { [fieldId]: _dropped, ...rest } = all;
      return rest;
    });
  };

  const isDirty = (field: Field): boolean => {
    const draft = drafts[field.id];
    if (draft === undefined) return false;
    return (
      draft.name !== field.name ||
      draft.note !== (field.note ?? "") ||
      draft.group !== (field.group_name ?? "")
    );
  };

  const openCard = (field: Field): void => {
    setEditingId(field.id);
    setAskDiscard(false);
    setFailedId(null);
    setDrafts((all) => (all[field.id] === undefined ? { ...all, [field.id]: formOf(field) } : all));
  };

  const closeCard = (fieldId: number, discard: boolean): void => {
    if (discard) dropDraft(fieldId);
    setEditingId(null);
    setAskDiscard(false);
    returnFocus.current = true;
  };

  /** Escape and «Отмена» go through here: unsaved edits ask instead of disappearing. */
  const requestClose = (field: Field): void => {
    if (isDirty(field) && !askDiscard) {
      setAskDiscard(true);
      return;
    }
    closeCard(field.id, true);
  };

  const patch = async (field: Field, body: FieldPatch): Promise<Field | null> => {
    setSaving(true);
    const updated = await onUpdateField(field.id, body);
    setSaving(false);
    setFailedId(updated === null ? field.id : null);
    return updated;
  };

  const save = async (field: Field): Promise<void> => {
    const draft = draftOf(field);
    if (draft.name.trim() === "") return;
    // One PATCH for the three typed properties: they are edited together in one form and
    // saved by one button, so splitting them would be three round trips and three ways to
    // half-succeed.
    const updated = await patch(field, {
      name: draft.name.trim(),
      note: draft.note.trim(),
      group_name: matchExistingGroup(normaliseGroup(draft.group), existingGroups),
    });
    if (updated === null) return;
    dropDraft(field.id);
    setEditingId(null);
    setAskDiscard(false);
    setSavedNotice(true);
  };

  const cyclePriority = async (field: Field): Promise<void> => {
    // Only `priority`. One PATCH used to carry `{name, priority, note}`, so marking a field
    // for Saturday rewrote its name and its note along the way.
    await patch(field, { priority: nextPriority(field.priority) });
  };

  const archive = async (field: Field): Promise<void> => {
    const updated = await patch(field, { archived: true });
    if (updated === null) return;
    dropDraft(field.id);
    setEditingId(null);
    setAskDiscard(false);
    setJustArchivedId(field.id);
    // Confirmation by MOVEMENT: the row travels into the archived group in front of you and
    // carries the way back on it. With the archive closed it simply vanished — no word, no
    // undo, nowhere to look, which is exactly the reported "просто блик и всё".
    if (!showArchived) onShowArchived(true);
  };

  const remove = async (field: Field): Promise<void> => {
    setDeleteArmedId(null);
    setSaving(true);
    const gone = await onDeleteField(field.id);
    setSaving(false);
    // A refusal stays under the button that was pressed, the same path the archive uses.
    setFailedId(gone ? null : field.id);
    if (!gone) return;
    dropDraft(field.id);
    setEditingId(null);
    setAskDiscard(false);
    setDeletedNotice(true);
  };

  const unarchive = async (field: Field): Promise<void> => {
    const updated = await patch(field, { archived: false });
    if (updated !== null) setJustArchivedId(null);
  };

  const firstLoad = loading && fields.length === 0;
  const firstError = error !== null && fields.length === 0;
  /** A refetch is not a first load. Both keep the list on screen and name themselves instead. */
  const stale = loading && fields.length > 0;
  const staleFailed = error !== null && fields.length > 0;

  const head = (
    <div className="k-sidebar__head">
      <span className="k-label">{T.fieldsPanel.title}</span>
      <span className="k-spacer" />
      {stale ? (
        // The same slot, the same size: the number does not lie, it says why it is not there.
        <span className="k-hint k-sidebar__stale">{T.fieldsPanel.updating}</span>
      ) : (
        !firstLoad && !firstError && <span className="k-hint k-mono">{live.length}</span>
      )}
    </div>
  );

  if (firstLoad) {
    return (
      <aside className="k-sidebar" aria-label={T.fieldsPanel.title} data-testid="field-list">
        {head}
        <div className="k-sidebar__body">
          <p className="k-caption">{T.common.loading}</p>
        </div>
      </aside>
    );
  }

  if (firstError) {
    return (
      <aside className="k-sidebar" aria-label={T.fieldsPanel.title} data-testid="field-list">
        {head}
        <div className="k-sidebar__body">
          <div className="k-note k-note--error" role="alert">
            {error}
          </div>
          <button
            type="button"
            className="k-btn k-btn--block"
            onClick={onRetry}
            style={{ marginTop: "var(--gap-2)" }}
          >
            {T.common.retry}
          </button>
        </div>
      </aside>
    );
  }

  const card = (field: Field) => {
    const draft = draftOf(field);

    return (
      <form
        className="k-sidebar__editor"
        onSubmit={(event) => {
          event.preventDefault();
          void save(field);
        }}
        // On the CONTAINER, not on `window`: there are already three global Escape listeners
        // with no contract of precedence between them, and a fourth would settle the argument
        // by mount order. One of the three also swallows Escape without checking whether text
        // is being typed.
        onKeyDown={(event) => {
          if (event.key !== "Escape") return;
          event.preventDefault();
          event.stopPropagation();
          requestClose(field);
        }}
      >
        <div className="k-fieldcard k-fieldcard--panel k-scroll">
          <span className="k-fieldcard__subject">{field.name}</span>

          <label className="k-field">
            <span className="k-label">{T.fieldCard.nameLabel}</span>
            <input
              className="k-input"
              ref={nameInput}
              value={draft.name}
              maxLength={NAME_MAX_LENGTH}
              placeholder={T.fieldCard.namePlaceholder}
              onChange={(event) => setDraft(field.id, { name: event.target.value }, draft)}
            />
          </label>

          {/* Second, between the name and the note: a group is a property of the field in the
            same sense the name is, and it is the one that decides where the row will stand.
            The hint is not decoration — Firefox shows nothing at all for a datalist until
            something is typed, so without it the existing names would be invisible on the
            one browser where guessing them matters. */}
          <label className="k-field">
            <span className="k-label">{T.fieldCard.groupLabel}</span>
            <input
              className="k-input"
              list={groupListId}
              value={draft.group}
              maxLength={GROUP_MAX_LENGTH}
              placeholder={T.fieldCard.groupPlaceholder}
              onChange={(event) => setDraft(field.id, { group: event.target.value }, draft)}
            />
          </label>
          {/* Outside the label on purpose: inside, the list of existing names would be read
            out as part of the box's own name on every focus. */}
          {existingGroups.length > 0 && (
            <p className="k-hint">{T.fieldCard.groupHint(existingGroups.join(", "))}</p>
          )}
          <datalist id={groupListId}>
            {existingGroups.map((name) => (
              <option key={name} value={name} />
            ))}
          </datalist>

          <label className="k-field">
            <span className="k-label">{T.fieldCard.noteLabel}</span>
            <textarea
              className="k-textarea"
              rows={3}
              maxLength={NOTE_MAX_LENGTH}
              value={draft.note}
              placeholder={T.fieldCard.notePlaceholder}
              onChange={(event) => setDraft(field.id, { note: event.target.value }, draft)}
            />
          </label>

          {/* ЗАМЕТКИ — СТРОКА-УКАЗАТЕЛЬ, А НЕ ВТОРОЙ СПИСОК. Сами заметки живут в консоли, на
              своей вкладке, и останутся там: второй список карточек в колонке 300 px, под
              которой лежит форма правки, — это две панели одного поля на одном экране. Здесь
              стоит счёт и дорога к ним: нажатие ПЕРЕКЛЮЧАЕТ ВКЛАДКУ (прокрутки больше нет —
              панель открывается сверху и целиком). При нуле строки нет вовсе: пустой счётчик
              ни о чём не сообщает. */}
          {noteCount > 0 && (
            <div className="k-field k-fieldcard__photos">
              {/* ПОДПИСИ НАД КНОПКОЙ НЕТ: «Заметки» ярлыком и «Заметки 4» на кнопке под ним —
                  это одно слово дважды подряд, из которых нажимается одно. Кнопка называет
                  предмет сама, и этого довольно. */}
              {/* Счёт — внутри кнопки, а не рядом с ней: разделить их значило бы поставить
                  на строку два органа, из которых нажимается один. Слова кнопки — имя
                  предмета: «Показать» без предмета в колонке с «Показать архив» ниже
                  называло бы неизвестно что. */}
              <button
                type="button"
                className="k-btn k-btn--compact"
                data-testid="block-notes"
                onClick={onShowNotes}
              >
                {T.markers.title} <span className="k-mono">{noteCount}</span>
              </button>
            </div>
          )}

          {/* THE TRIP MARK IS EDITED HERE, and it is the one control in this form that takes
            effect on its own click rather than on «Сохранить»: it always did (one PATCH
            carrying `priority` alone), and it moved here from the row when the strip that
            used to hold it went. The value IS the label, so the button says which state it is
            in rather than which state it would set. */}
          <div className="k-field k-fieldcard__prio">
            <span className="k-label">{T.fieldCard.priorityLabel}</span>
            <button
              type="button"
              className="k-btn"
              aria-label={`${T.fieldCard.priorityLabel}: ${PRIORITY_LABEL[field.priority]}`}
              disabled={saving}
              onClick={() => void cyclePriority(field)}
            >
              {PRIORITY_LABEL[field.priority]}
            </button>
            {/* The cycle has to be told: a button whose label is its value reads as a stuck
                dropdown, and this is the one control here that saves on its own click. */}
            <p className="k-hint">{T.fieldCard.priorityHint}</p>
          </div>

          {/* THE TWO WAYS OUT OF THE LIST, READ TOGETHER. Both are lifecycle acts and neither
            rides along with a rename — each takes effect on its own click. They are one block
            because the choice between them is made once and needs both prices side by side:
            archiving keeps everything that was computed, deleting takes it. The dotted rule
            between them is this project's own mark for "same subject, different answer". */}
          <div className="k-fieldcard__lifecycle">
            <span className="k-label">{T.fieldCard.lifecycleLabel}</span>
            <p className="k-hint">{T.fieldCard.archiveHint}</p>
            <button
              type="button"
              className="k-btn"
              disabled={saving}
              onClick={() => void archive(field)}
            >
              {T.fieldCard.archive}
            </button>

            <div className="k-fieldcard__danger-why">
              <p className="k-hint">{T.fieldCard.deleteHint(field.run_count ?? 0)}</p>
            </div>
            {deleteArmedId === field.id ? (
              /* `--error` and not `--warn`: the warning tone is already spoken for by
               «Изменения не сохранены», and an irreversible question must not look like an
               unsaved one. The same question, the same two words as the map toolbar. */
              <div className="k-note k-note--error" role="alert">
                <p>{T.tools.deleteAsk(field.name)}</p>
                <div className="k-row k-row--wrap">
                  <button
                    type="button"
                    className="k-btn k-btn--danger k-btn--compact"
                    disabled={saving}
                    onClick={() => void remove(field)}
                  >
                    {T.tools.deleteYes}
                  </button>
                  <button
                    type="button"
                    className="k-btn k-btn--ghost k-btn--compact"
                    onClick={() => setDeleteArmedId(null)}
                  >
                    {T.tools.deleteNo}
                  </button>
                </div>
              </div>
            ) : (
              <button
                type="button"
                className="k-btn k-btn--danger k-btn--compact"
                disabled={saving}
                onClick={() => setDeleteArmedId(field.id)}
              >
                {T.tools.delete}
              </button>
            )}
          </div>
        </div>

        {/* The question stands ABOVE the pinned foot, not inside the scroller: it is raised by
            Escape, and Escape can be pressed with the form scrolled anywhere. Its answer takes
            the foot's second slot, so «Сохранить» exists once whichever question is open. */}
        {askDiscard && (
          <p className="k-note k-note--warn" role="alert">
            {T.fieldCard.unsavedTitle}
          </p>
        )}

        {/* The pinned foot, in the slot «Выбрать все» holds in list mode (UX_SPEC §13.1): the
            two ways out of an editor may not be scrolled off the bottom of a long note. */}
        <div className="k-sidebar__foot">
          <button type="submit" className="k-btn" disabled={saving || draft.name.trim() === ""}>
            {saving ? T.fieldCard.saving : T.fieldCard.save}
          </button>
          {/* A real border, not `--ghost`: an editor with two equal ways out cannot have one of
              them invisible at rest — the same defect the pencil had. */}
          {askDiscard ? (
            <button type="button" className="k-btn" onClick={() => closeCard(field.id, true)}>
              {T.fieldCard.discard}
            </button>
          ) : (
            <button type="button" className="k-btn" onClick={() => requestClose(field)}>
              {T.fieldCard.cancel}
            </button>
          )}
        </div>
      </form>
    );
  };

  const row = (field: Field) => {
    const entry = queue.find((item) => item.fieldId === field.id);
    const tag = priorityTag(field.priority);
    // An archived field is refused by every path that costs something (409 from the run, the
    // classification and the tiles), so neither the row nor its checkbox may hand
    // it to any of them. It is not selectable either, so its edit slot stays empty — instead it
    // carries the one control it needs, in words: «Вернуть в список». Four steps (open the
    // archive, find the row, hit an unnamed pencil, scroll a card) become one.
    const archivedRow = field.archived_at != null;
    const selected = field.id === selectedFieldId;
    const dot = queueDot(entry?.status);
    return (
      <div key={field.id}>
        <div className="k-fields__row">
          <input
            type="checkbox"
            aria-label={`${T.fieldsPanel.selected}: ${field.name}`}
            checked={checkedFieldIds.includes(field.id)}
            disabled={archivedRow}
            onChange={() => onToggleCheck(field.id)}
          />
          <button
            type="button"
            className={`k-fieldrow${priorityEdge(field.priority)}`}
            ref={selected ? selectedRow : undefined}
            aria-current={selected}
            disabled={archivedRow}
            // Слово метки — ещё и в `title`: в строке оно набрано мелким и стоит в самом
            // правом столбце, где его читают не первым. Раскладку это не трогает.
            title={
              archivedRow
                ? T.fieldCard.archivedRowWhy
                : tag
                  ? `${T.fieldCard.priorityLabel}: ${tag}`
                  : undefined
            }
            onClick={() => onSelect(field.id)}
            // Accelerators, both additional to the visible control and neither instead of it.
            // Enter is not hijacked on an unselected row: that is the one keyboard path that
            // already works. Enter goes one step deeper — first press selects, second opens.
            onDoubleClick={() => {
              onSelect(field.id);
              openCard(field);
            }}
            onKeyDown={(event) => {
              if (event.key !== "Enter" || !selected) return;
              event.preventDefault();
              openCard(field);
            }}
          >
            <span className="k-fieldrow__name">{field.name}</span>
            <span className="k-fieldrow__area">
              {hectares(field.area_ha)} {T.tools.hectares}
            </span>
            {/* Silence, not «не анализировано». On a fresh account that word stood on all
                twelve rows, saying the same thing twelve times about a list where it is the
                default. The half-line appears when there is something in it (UX_SPEC §7.4),
                and it says the STRONGEST thing it has: «ответ 21 июля», когда удачный прогон
                есть, и голый счёт попыток, когда все они кончились ничем. Счёт при готовом
                ответе был мерой нашей работы, а не свойством поля. */}
            {field.last_run?.has_answer ? (
              <span className="k-fieldrow__runs">
                {/* `formatFull`, не `formatDayMonth` (lastanswer-year): ростер мешает эпохи,
                    и «ответ на 10 сентября» прошлого года без года читался свежим — а после
                    Нового года и вовсе будущим. */}
                {T.fieldsPanel.lastAnswer(formatFull(field.last_run.analysis_date))}
              </span>
            ) : field.run_count ? (
              <span className="k-fieldrow__runs">{T.fieldsPanel.runs(field.run_count)}</span>
            ) : null}
            {/* One slot, one word: an archived row says so where a live row says its
                priority — the mark is moot on a field that cannot be run anyway. */}
            <span className="k-fieldrow__prio">
              {archivedRow ? T.fieldCard.archivedTag : (tag ?? "")}
              {dot && <span className={dot} aria-hidden="true" />}
            </span>
          </button>

          {/* The way into the editor, on a place the row reserves whether it is shown or not
              (UX_SPEC §13.2). Shown on hover and on the selected row — CSS, so that hovering
              does not re-render twenty rows. */}
          <span className="k-fields__editslot" data-shown={selected && !archivedRow}>
            {!archivedRow && (
              <button
                type="button"
                className="k-btn k-fields__editbtn"
                ref={selected ? editButton : undefined}
                aria-label={`${T.fieldCard.edit}: ${field.name}`}
                title={T.fieldCard.edit}
                onClick={() => {
                  onSelect(field.id);
                  openCard(field);
                }}
              >
                <Glyph d={glyph.edit} />
              </button>
            )}
          </span>
        </div>

        {/* THE NOTE HAS TO BE READ, not just stored: it was written by the card and rendered
            nowhere else, so the one thing that says WHY a field is interesting was invisible
            until you opened an editor on each of twenty rows in turn. One clamped line under
            every row that has one — see `.k-fieldrow__note` for why it is not the selected
            row's alone any more. */}
        {field.note && (
          <p className="k-fieldrow__note" title={field.note}>
            {field.note}
          </p>
        )}

        {archivedRow && (
          <div className="k-fieldrow__actions">
            <button
              type="button"
              className="k-btn k-btn--compact"
              disabled={saving}
              onClick={() => void unarchive(field)}
            >
              {T.fieldCard.unarchive}
            </button>
          </div>
        )}

        {/* The refusal stands under the thing that was pressed, not 1000 px away in the
            console: on a full account «Вернуть в список» answered 409 and the sidebar's text
            was character-for-character unchanged. Below the card, so it is next to the archive
            button when the card is the one that failed. */}
        {failedId === field.id && actionError !== null && (
          <p className="k-note k-note--error" role="alert">
            {actionError}
          </p>
        )}
      </div>
    );
  };

  /**
   * THE EDITOR IS THE COLUMN'S SECOND MODE (UX_SPEC §13.1), not a block inserted into the
   * first. Inline, it displaced every row under it — up to 320 px from one press — and on the
   * last rows it opened below the fold. Nothing is lost by replacing the list: the field being
   * edited is the selected one, and the selected one is already drawn on the map and described
   * by the console. The way back is «Отмена», Escape, or a click on any row once the list is
   * back. The head stays, so the column never stops saying what it is.
   */
  const editingField = editingId === null ? null : (fields.find((f) => f.id === editingId) ?? null);
  if (editingField !== null) {
    return (
      <aside className="k-sidebar" aria-label={T.fieldsPanel.title} data-testid="field-list">
        {head}
        {card(editingField)}
        {failedId === editingField.id && actionError !== null && (
          <p className="k-note k-note--error" role="alert">
            {actionError}
          </p>
        )}
      </aside>
    );
  }

  return (
    <aside className="k-sidebar" aria-label={T.fieldsPanel.title} data-testid="field-list">
      {head}

      <div className="k-sidebar__tools">
        <label className="k-hint" htmlFor="k-field-sort">
          {T.fieldsPanel.sortLabel}
        </label>
        <select
          id="k-field-sort"
          className="k-select"
          value={sort}
          onChange={(event) => setSort(event.target.value as SortKey)}
        >
          <option value="priority">{T.fieldsPanel.sortPriority}</option>
          <option value="name">{T.fieldsPanel.sortName}</option>
          <option value="area">{T.fieldsPanel.sortArea}</option>
        </select>
        <span className="k-spacer" />
        {/* Two positions, two words. With one label for both, the only thing that changed on
            a press was the ochre `aria-pressed` frame — a latch whose own words say nothing
            about which way it is thrown, next to an eye whose pupil is filled or hollow.
            Not `--ghost` either: a latch has to be an organ in BOTH positions, and a border
            that appears on press reads as a control that appeared. Its pressed tone is
            neutral, set in layout.css — the ochre fill belongs to «Анализ поля» alone. */}
        <button
          type="button"
          className="k-btn"
          aria-pressed={showArchived}
          onClick={() => onShowArchived(!showArchived)}
        >
          {showArchived ? T.fieldsPanel.archiveHide : T.fieldsPanel.archiveShow}
        </button>
      </div>

      {/* A refetch that failed has no right to carry twenty good rows off the screen with it. */}
      {staleFailed && (
        <div className="k-sidebar__stalefail" role="alert">
          <span>{T.fieldsPanel.staleFailed}</span>
          <button type="button" className="k-btn k-btn--ghost k-btn--compact" onClick={onRetry}>
            {T.common.retry}
          </button>
        </div>
      )}

      <div className="k-fields k-scroll" aria-busy={stale}>
        {live.length === 0 && (
          <div className="k-empty" role="status">
            <span className="k-empty__title">
              {archived.length > 0 ? T.fieldsPanel.liveNone : T.fieldsPanel.none}
            </span>
            {/* One line saying what to do, and the same sentence the console says it with:
                the first screen of every new account is this one. */}
            {archived.length === 0 && <p className="k-hint">{T.firstRun.step}</p>}
          </div>
        )}

        {/* Flat until somebody names a group — the headings are not a permanent frame, they
            appear when there is a distinction to draw. Folded groups keep their count on the
            heading, so a closed group still says how much is inside it. */}
        {hasGroups
          ? groups.map((group) => {
              const key = group.name ?? "";
              const open = !closedGroups.includes(key);
              return (
                <details key={key} className="k-fields__section" open={open}>
                  <summary
                    className="k-fields__group"
                    aria-expanded={open}
                    // Controlled by hand rather than by the element's own toggle: `open` is
                    // React state here, and letting the browser flip it too gives one
                    // attribute two owners.
                    onClick={(event) => {
                      event.preventDefault();
                      setClosedGroups((current) =>
                        current.includes(key)
                          ? current.filter((name) => name !== key)
                          : [...current, key],
                      );
                    }}
                  >
                    {group.name ?? T.fieldsPanel.groupNone}
                    <span className="k-mono"> · {group.fields.length}</span>
                  </summary>
                  {group.fields.map(row)}
                </details>
              );
            })
          : live.map(row)}

        {/* Rendered whatever the number of live fields is. It used to be short-circuited by
            `fields.length === 0`, so on an empty account — the first screen anybody sees —
            the DOM before the press and after it were identical. */}
        {showArchived && (
          <>
            <div className="k-fields__group" ref={archivedGroup}>
              {T.fieldsPanel.archivedGroup}
              {archived.length > 0 && <span className="k-mono"> · {archived.length}</span>}
            </div>
            {archived.length > 0 && (
              // Once, at the group. Twenty tooltips are read by nobody, and `title` on a
              // `disabled` control is unreliable — some engines send it no pointer events at
              // all, so today's reason could fail to appear even once.
              <p className="k-fields__why">{T.fieldsPanel.archivedGroupWhy}</p>
            )}
            {justArchivedId !== null && (
              <p className="k-caption" role="status">
                {T.fieldCard.archivedDone}
              </p>
            )}
            {archived.length === 0 ? (
              <p className="k-caption" role="status">
                {T.fieldsPanel.archivedNone}
              </p>
            ) : (
              archived.map(row)
            )}
          </>
        )}
      </div>

      {(live.length > 0 || savedNotice || deletedNotice) && (
        <div className="k-sidebar__foot">
          {/* THE ACT COMES FIRST, and it is the only solid button in the foot: «Выбрать все»
              and «Снять выбор» change the SELECTION, this one spends it. It appears from one
              tick, not from two — a batch of one is still the answer to "run these", and a
              button that waits for a second tick before existing is a rule nobody can see. */}
          {checkedFieldIds.length > 0 && (
            <button type="button" className="k-btn" disabled={runBusy} onClick={onRunBatch}>
              {T.run.startBatch(checkedFieldIds.length)}
            </button>
          )}
          {live.length > 0 && (
            <button type="button" className="k-btn k-btn--ghost" onClick={onCheckAll}>
              {T.fieldsPanel.selectAll}
            </button>
          )}
          {checkedFieldIds.length > 0 && (
            <button type="button" className="k-btn k-btn--ghost" onClick={onClearChecked}>
              {T.fieldsPanel.clearSelection}
            </button>
          )}
          {savedNotice && (
            <span className="k-hint" role="status">
              {T.fieldsPanel.saved}
            </span>
          )}
          {/* The row is gone and so is the card it was deleted from, so the only place left
              to say it is the foot. Announced, not just printed: the thing that changed is
              an absence. */}
          {deletedNotice && (
            <span className="k-hint" role="status">
              {T.fieldCard.deleted}
            </span>
          )}
        </div>
      )}
    </aside>
  );
}
